import React, { useState, useCallback } from 'react';
import './WaveformCommentMarkers.css';
import { CommentMarker } from '../services/social';

interface WaveformCommentMarkersProps {
  markers: CommentMarker[];
  duration: number; // Total duration in seconds
  onMarkerClick: (marker: CommentMarker) => void;
  onMarkerHover?: (marker: CommentMarker | null) => void;
  height?: number;
}

/**
 * WaveformCommentMarkers renders clickable comment markers overlaid on a waveform.
 * Markers are positioned horizontally based on their timestamp relative to duration.
 * Clicking a marker triggers onMarkerClick (typically to seek and show comments).
 */
const WaveformCommentMarkers: React.FC<WaveformCommentMarkersProps> = ({
  markers,
  duration,
  onMarkerClick,
  onMarkerHover,
  height = 80,
}) => {
  const [hoveredMarker, setHoveredMarker] = useState<CommentMarker | null>(null);

  const handleMouseEnter = useCallback((marker: CommentMarker) => {
    setHoveredMarker(marker);
    onMarkerHover?.(marker);
  }, [onMarkerHover]);

  const handleMouseLeave = useCallback(() => {
    setHoveredMarker(null);
    onMarkerHover?.(null);
  }, [onMarkerHover]);

  const handleClick = useCallback((marker: CommentMarker, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent waveform click-to-seek
    onMarkerClick(marker);
  }, [onMarkerClick]);

  // Format timestamp for display (MM:SS)
  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (markers.length === 0 || duration <= 0) {
    return null;
  }

  return (
    <div className="waveform-comment-markers" style={{ height: `${height}px` }}>
      {markers.map((marker) => {
        // Calculate position as percentage of width
        const positionPercent = (marker.timestampMs / (duration * 1000)) * 100;

        // Clamp to valid range
        const clampedPosition = Math.max(0, Math.min(positionPercent, 100));

        const isHovered = hoveredMarker?.id === marker.id;

        return (
          <div
            key={marker.id}
            className={`comment-marker ${isHovered ? 'comment-marker-hovered' : ''}`}
            style={{ left: `${clampedPosition}%` }}
            onClick={(e) => handleClick(marker, e)}
            onMouseEnter={() => handleMouseEnter(marker)}
            onMouseLeave={handleMouseLeave}
            role="button"
            tabIndex={0}
            aria-label={`${marker.commentCount} comment${marker.commentCount > 1 ? 's' : ''} at ${formatTime(marker.timestampMs)}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onMarkerClick(marker);
              }
            }}
          >
            {/* Marker pin */}
            <div className="comment-marker-pin">
              <svg
                width="12"
                height="16"
                viewBox="0 0 12 16"
                fill="currentColor"
              >
                <path d="M6 0C2.69 0 0 2.69 0 6c0 4.5 6 10 6 10s6-5.5 6-10c0-3.31-2.69-6-6-6zm0 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
              </svg>
              {/* Badge for multiple comments */}
              {marker.commentCount > 1 && (
                <span className="comment-marker-count">{marker.commentCount}</span>
              )}
            </div>

            {/* Tooltip preview on hover */}
            {isHovered && (
              <div className="comment-marker-tooltip">
                <div className="tooltip-header">
                  <span className="tooltip-time">{formatTime(marker.timestampMs)}</span>
                  <span className="tooltip-count">
                    {marker.commentCount} comment{marker.commentCount > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="tooltip-preview">
                  {marker.comments[0].username}: {marker.comments[0].content.slice(0, 60)}
                  {marker.comments[0].content.length > 60 ? '...' : ''}
                </div>
                {marker.commentCount > 1 && (
                  <div className="tooltip-more">
                    Click to see all comments
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WaveformCommentMarkers;
